/**
 * Firebase Functions utilities with proper authentication
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { createComponentLogger } from './logger';

const logger = createComponentLogger('Functions');

/**
 * Call a Firebase Function with proper error handling
 */
export const callFunction = async (functionName: string, data: unknown) => {
  try {
    const fn = httpsCallable(functions.value, functionName);
    const result = await fn(data);
    return result.data;
  } catch (error) {
    logger.error(`Error calling function ${functionName}:`, error);

    const code =
      error !== null && typeof error === 'object' && 'code' in error
        ? (error as { code: unknown }).code
        : undefined;
    const message =
      error instanceof Error ? error.message : undefined;

    // Re-throw with more specific error information.
    //
    // S236: the replacement carries the server's CODE and its own MESSAGE as well, so a caller
    // can tell one refusal from another. Rewriting every `permission-denied` to "Permission
    // denied. Check your credentials." made "Incorrect PIN" indistinguishable from "not this
    // child's parent" — and the screen-time screen could not relock on a wrong PIN.
    const friendly =
      code === 'functions/unauthenticated'
        ? 'Authentication required. Please log in again.'
        : code === 'functions/permission-denied'
          ? 'Permission denied. Check your credentials.'
          : code === 'functions/internal'
            ? 'Server error. Please try again.'
            : code === 'functions/unavailable'
              ? 'Service temporarily unavailable. Please try again.'
              : message || 'Unknown error occurred';
    throw Object.assign(new Error(friendly), {
      code: typeof code === 'string' ? code.replace(/^functions\//, '') : undefined,
      serverMessage: message,
    });
  }
};

/**
 * Chat history message for multi-turn conversation context
 */
export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Chat with Game Buddy. The server chooses the model (docs/DECISIONS.md § 17); the client sends
 * only the conversation. The provider/model arguments this used to take were removed 2026-10-05.
 */
export const chatWithClaude = async (
  projectId: string,
  message: string,
  context: unknown,
  chatHistory?: ChatHistoryMessage[]
) => {
  return await callFunction('chatWithClaude', {
    projectId,
    message,
    context,
    chatHistory
  });
};